import { applyPatch } from 'diff'
import yaml from 'js-yaml'
import { sanitizeCardDescription } from '@/utils/sanitize'
import CryptoJS from 'crypto-js'
import _ from 'lodash'

export const renderTree = (fileObject, indent = '', isRoot = true) => {
  let output = ''
  const keys = Object.keys(fileObject)

  if (isRoot) {
    const rootName = keys[0]
    output += `${rootName}/\n`
    return output + renderTree(fileObject[rootName], '', false)
  }

  keys.forEach((key, index) => {
    const isLast = index === keys.length - 1
    const prefix = isLast ? '└── ' : '├── '
    const nestedIndent = isLast ? '    ' : '│   '

    if (Object.keys(fileObject[key]).length > 0) { // It's a directory
      output += `${indent}${prefix}${key}/\n`
      output += renderTree(fileObject[key], indent + nestedIndent, false)
    } else { // It's a file
      output += `${indent}${prefix}${key}\n`
    }
  })

  return output
}

export const flattenTree = (tree, opened, basePath = '', depth = 0) => {
  const result = []

  Object.entries(tree)
    .sort(([nameA, childrenA], [nameB, childrenB]) => {
      const isFolderA = childrenA && typeof childrenA === 'object' && Object.keys(childrenA).length > 0
      const isFolderB = childrenB && typeof childrenB === 'object' && Object.keys(childrenB).length > 0

      if (isFolderA !== isFolderB) {
        return isFolderA ? -1 : 1 // folders first
      }

      return nameA.localeCompare(nameB) // alphabetical by name
    })
    .forEach(([name, children]) => {
      const fullPath = basePath ? `${basePath}/${name}` : name
      const isFolder = children && typeof children === 'object' && Object.keys(children).length > 0
      const isOpened = isFolder && opened.includes(fullPath)

      result.push({
        name,
        path: fullPath,
        fullPath,
        isFolder,
        isOpened,
        depth
      })

      if (isFolder && isOpened) {
        result.push(...flattenTree(children, opened, fullPath, depth + 1))
      }
    })

  return result
}

const isObject = (value) => value && typeof value === 'object' && !Array.isArray(value)

export const collapsePaths = (node) => {
  // If the node is not an object or is empty (like a file), return it as is.
  if (!isObject(node) || Object.keys(node).length === 0) {
    return node
  }

  return Object.keys(node).reduce((acc, key) => {
    let currentPath = key
    let currentNode = node[key]

    // Keep traversing as long as the current node is an object with exactly one key.
    // This identifies a chain of single nested folders.
    const nodeKeys = Object.keys(currentNode)
    while (isObject(currentNode) && nodeKeys.length === 1) {
      const singleKey = nodeKeys[0]

      // Stop if the single child is a file (an empty object).
      if (Object.keys(currentNode[singleKey]).length === 0) {
        break
      }

      // Append the nested folder to the path and move down the tree.
      currentPath += `/${singleKey}`
      currentNode = currentNode[singleKey]

      // Update keys for the next iteration check.
      Object.assign(nodeKeys, Object.keys(currentNode))
    }

    // Assign the processed (and recursively collapsed) node to the new collapsed path.
    acc[currentPath] = collapsePaths(currentNode)
    return acc
  }, {})
}

export const groupFilesByTags = (files) => {
  const tagGroups = {}

  for (const file of files) {
    const tags = file.tags || []
    for (const tag of tags) {
      if (!tagGroups[tag]) {
        tagGroups[tag] = []
      }
      tagGroups[tag].push(file)
    }
  }

  return tagGroups
}

export const applyGitDiff = (originalContent, diffString) => {
  // The diff library's applyPatch function expects the diff to not have the --- a/file and +++ b/file headers.
  // We also need to ensure consistent line endings for the original content and the patch.
  const cleanDiffString = diffString
    .split('\n')
    .slice(2) // Remove the first two lines (--- and +++)
    .map(line => line.replace(/\u00A0/g, ' '))
    .join('\n')

  // Ensure consistent line endings for original content
  const normalizedOriginalContent = originalContent.replace(/\r\n/g, '\n')

  const patchedContent = applyPatch(normalizedOriginalContent, cleanDiffString)

  if (patchedContent === false) {
    console.error('Failed to apply patch. The diff may not be applicable to the original content.')
    return false
  }
  return patchedContent
}

export const getLanguageFromFilename = (filename) => {
  const mapExtensionToLanguage = {
    js: 'javascript',
    cs: 'csharp',
    json: 'json',
    md: 'markdown',
    yaml: 'yaml',
    env: 'shell',
    '': 'plaintext'
  }

  const extension = filename.split('.').pop()
  // TODO: this function seems to run every time chatGPT gives us a response. It should be optimized.
  // console.log(extension, mapExtensionToLanguage[extension])
  return mapExtensionToLanguage[extension] || 'plaintext'
}

export const toCamelCase = (str) => {
  const sanitizedStr = str.replace(/[^a-zA-Z0-9]+(.)?/g, (match, chr) => chr ? chr.toUpperCase() : '').trim()
  return sanitizedStr.charAt(0).toLowerCase() + sanitizedStr.slice(1)
}

export const convertJsonToSwaggerYaml = (jsonInput) => {
  const swaggerSpec = {
    openapi: '3.0.0',
    info: {
      title: jsonInput.name,
      version: '1.0.0',
      description: `API for managing a ${jsonInput.entityInfo.name}.`
    },
    servers: [
      {
        url: 'http://localhost:3000',
        description: 'Local Development Server'
      }
    ],
    paths: {},
    components: {
      schemas: {}
    }
  }

  // Find the primary key from the entity info
  // const primaryKeyField = jsonInput.entityInfo.dataFields.find(field => field.primaryKey)
  // const primaryKeyName = primaryKeyField ? primaryKeyField.name : null
  // const primaryKeyCamelCase = primaryKeyName ? toCamelCase(primaryKeyName) : null

  // Create a schema for the main entity using camelCase properties
  const entitySchemaName = jsonInput.entityInfo.name.replace(/\s/g, '')
  swaggerSpec.components.schemas[entitySchemaName] = {
    type: 'object',
    properties: {}
  }
  jsonInput.entityInfo.dataFields.forEach(field => {
    const propertyName = toCamelCase(field.name)
    let property = {}
    if (field.relatedEntityId) {
      const relatedEntityName = jsonInput.relatedEntityInfo.find(re => re.id === field.relatedEntityId)?.name || field.name
      if (field.cardinality === 'one-to-one') {
        property.$ref = `#/components/schemas/${relatedEntityName}`
      }
      if (field.cardinality === 'one-to-many') {
        property.type = 'array'
        property.items = { $ref: `#/components/schemas/${relatedEntityName}` }
      }
    } else {
      property = {
        type: 'string',
        example: field.exampleData[0]
      }

      if (field.primaryKey) {
        property.description = `Unique identifier for the ${jsonInput.entityInfo.name} (primary key).`
      }
    }
    swaggerSpec.components.schemas[entitySchemaName].properties[propertyName] = property
    // swaggerSpec.components.schemas[entitySchemaName].properties[propertyName] = {
    //   type: field.relatedEntityId ? undefined : 'string', // Defaulting to string
    //   example: field.relatedEntityId ? undefined : field.exampleData[0],
    //   $ref: field.relatedEntityId ? `#/components/schemas/${field.name}` : undefined
    // }
  })

  const relatedEntities = jsonInput.relatedEntityInfo ? jsonInput.relatedEntityInfo : []
  for (const relatedEntity of relatedEntities) {
    const relatedEntityName = relatedEntity.name.replace(/\s/g, '')
    relatedEntity.dataFields.forEach(field => {
      const propertyName = toCamelCase(field.name)
      swaggerSpec.components.schemas[relatedEntityName] = {
        type: 'object',
        properties: {}
      }
      swaggerSpec.components.schemas[relatedEntityName].properties[propertyName] = {
        type: field.relatedEntityId ? field.name : 'string', // Defaulting to string
        example: field.exampleData[0]
      }
    })
  }

  // Process commands to create POST endpoints
  jsonInput.commandInfo.forEach(command => {
    const path = `/${command.description.toLowerCase().replace(/\s/g, '-')}`
    const operation = {
      summary: command.description,
      tags: ['Commands'],
      responses: {
        200: { description: 'Success' },
        // 201: { description: 'Created' },
        400: { description: 'Bad Request' }
        // 500: { description: 'Internal Server Error' }
      }
    }

    const bodyDataFields = [...command.dataFields]

    // // If the primary key is in the command, treat it as a path parameter
    // if (primaryKeyName && command.dataFields.includes(primaryKeyName)) {
    //   path += `/{${primaryKeyCamelCase}}`
    //   operation.parameters = [{
    //     name: primaryKeyCamelCase,
    //     in: 'path',
    //     required: true,
    //     schema: {
    //       type: 'string'
    //     },
    //     description: `The ID of the ${jsonInput.entityInfo.name}.`
    //   }]
    //   // Remove primary key from the list of fields for the request body
    //   bodyDataFields = bodyDataFields.filter(field => field !== primaryKeyName)
    // }

    // Add a request body only if there are fields remaining
    if (bodyDataFields.length > 0) {
      operation.requestBody = {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: bodyDataFields.reduce((props, field) => {
                props[toCamelCase(field.name)] = { type: 'string' } // Convert to camelCase
                return props
              }, {})
            }
          }
        }
      }
    }

    swaggerSpec.paths[path] = { post: operation }
  })

  // Process read models to create GET endpoints
  jsonInput.readModelInfo.forEach(readModel => {
    let path = `/${readModel.description.toLowerCase().replace(/\s/g, '-')}`
    const operation = {
      summary: readModel.description,
      tags: ['Read Models'],
      responses: {
        200: {
          description: 'Successful response',
          content: {
            'application/json': {
              schema: {
                type: readModel.cardinality === 'one-to-one' ? 'object' : 'array',
                properties: readModel.dataFields.reduce((props, field) => {
                  props[toCamelCase(field.name)] = { type: 'string' } // Convert to camelCase
                  return props
                }, {})
              }
            }
          }
        },
        400: { description: 'Bad Request' }
        // 500: { description: 'Internal Server Error' }
      }
    }

    // // If the primary key is in the data fields, it's a path parameter
    // if (primaryKeyName && readModel.dataFields.includes(primaryKeyName)) {
    //   path += `/{${primaryKeyCamelCase}}`
    //   operation.parameters = [{
    //     name: primaryKeyCamelCase,
    //     in: 'path',
    //     required: true,
    //     schema: {
    //       type: 'string'
    //     },
    //     description: `The ID of the ${jsonInput.entityInfo.name}.`
    //   }]
    // }
    const filterTypeFields = readModel.dataFields.filter(f => f.type === 'filter')
    path += filterTypeFields.map(f => `/{${toCamelCase(f.name)}}`).join('')
    operation.parameters = filterTypeFields.map(f => ({
      in: 'path',
      name: toCamelCase(f.name),
      required: true,
      schema: { type: 'string' }
    }))

    // Check for duplicate paths and merge if necessary
    if (swaggerSpec.paths[path]) {
      Object.assign(swaggerSpec.paths[path], { get: operation })
    } else {
      swaggerSpec.paths[path] = { get: operation }
    }
  })

  // Convert the JavaScript object to a YAML string
  return yaml.dump(swaggerSpec)
}

export const toPascalCase = (str) => {
  if (!str) {
    return ''
  }

  return str.replace(/^\w|[A-Z]|\b\w/g, (word) => word.toUpperCase()).replace(/\s+/g, '')
}

export const identifySchemaType = (example) => {
  if (example === null || example === undefined) {
    return 'string'
  }
  if (!isNaN(example) && !isNaN(parseFloat(example))) {
    return Number.isInteger(parseFloat(example)) ? 'integer' : 'number'
  }
  if (typeof example === 'boolean' || example === 'true' || example === 'false') {
    return 'boolean'
  }
  return 'string'
}

const descriptionToPath = (description) => {
  if (!description) {
    return '/unknown-path'
  }

  return '/' + description.trim().toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-')
}

export const groupAndMergeReadModels = (rawReadModels, allEntities) => {
  const groupedReadModels = _.groupBy(rawReadModels, (rm) =>
    `${rm.entityId || rm.associatedEntity.id}-${rm.cardinality}`
  )

  return Object.values(groupedReadModels).map((group) => {
    const baseModel = group[0]
    const allIds = group.map((rm) => rm.id)
    const entityId = baseModel.entityId || baseModel.associatedEntity.id
    const entity = allEntities.find((e) => e.id === entityId)

    if (!entity) {
      return { ...baseModel, allIds, allDescriptions: group.map((rm) => rm.description) }
    }

    const fieldsMap = {}
    group.forEach((rm) => {
      (rm.dataFields || []).forEach((field) => {
        const existingField = fieldsMap[field.name]
        if (!existingField || (field.type === 'filter' && existingField.type !== 'filter')) {
          fieldsMap[field.name] = field
        }
      })
    })
    const allDataFields = Object.values(fieldsMap)

    return {
      ...baseModel,
      id: baseModel.id,
      description: baseModel.cardinality === 'one-to-many' ? `Get All ${entity.name}s` : `Get ${entity.name} by ID`,
      entityId,
      dataFields: allDataFields,
      allIds,
      allDescriptions: group.map((rm) => rm.description),
      associatedEntity: entity
    }
  })
}

export const convertWorkflowToSwaggerYaml = (workflowData, boundedContext) => {
  const swaggerSpec = {
    openapi: '3.0.3',
    info: {
      title: `${boundedContext.name} API Specification`,
      version: workflowData.version.toString(),
      description: boundedContext.description || `API documentation for the ${boundedContext.name} bounded context.`
    },
    servers: [{
      url: '/api/v1',
      description: 'Development Server'
    }],
    tags: [],
    paths: {},
    components: {
      schemas: {
        Error: {
          type: 'object',
          properties: {
            message: { type: 'string', example: 'An error occurred.' }
          }
        }
      },
      parameters: {},
      responses: {
        BadRequest: {
          description: 'Bad Request: The server cannot or will not process the request due to something that is perceived to be a client error.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/Error' } } }
        },
        NotFound: {
          description: 'Not Found: The server can not find the requested resource.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/Error' } } }
        },
        InternalServerError: {
          description: 'Internal Server Error: The server has encountered a situation it doesn\'t know how to handle.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/Error' } } }
        }
      }
    }
  }

  let contextEntities = (workflowData.dataModels || []).filter((entity) =>
    entity.boundedContext === boundedContext.id)

  const relatedEntities = []
  _.forEach(contextEntities, (entity) => {
    _.forEach(
      _.filter(entity.dataFields, f => f.relatedEntityId),
      (field) => {
        const entity = workflowData.dataModels.find(e => e.id === field.relatedEntityId)

        if (entity && !relatedEntities.some(e => e.id === entity.id)) {
          relatedEntities.push(entity)
        }
      }
    )
  })
  contextEntities = [...contextEntities, ...relatedEntities]

  const contextEntityMap = new Map(contextEntities.map(entity => [entity.id, entity]))

  const contextEvents = (workflowData.eventsJson || []).filter((event) => {
    if (!event.requirementsJson) {
      return false
    }
    const entityCard = event.requirementsJson.find(r => r.cardType.aggregateType === true)
    if (!entityCard) {
      return false
    }

    if (entityCard.entityId && contextEntityMap.has(entityCard.entityId)) {
      return true
    }

    return !entityCard.entityId && entityCard.boundedContext === boundedContext.id
  })

  const entities = {}

  contextEntities.forEach((entity) => {
    const entityNamePascal = toPascalCase(entity.name)
    const primaryKeyField = entity.dataFields.find((f) => f.primaryKey)
    entities[entity.id] = {
      ...entity,
      namePascal: entityNamePascal,
      nameCamel: toCamelCase(entity.name),
      primaryKey: primaryKeyField
        ? {
            name: primaryKeyField.name,
            nameCamel: toCamelCase(primaryKeyField.name)
          }
        : null
    }
  })

  const commands = []
  const readModels = []

  contextEvents.forEach((event) => {
    if (!event.requirementsJson) {
      return
    }
    const aggregateRequirement = event.requirementsJson.find(r => r.cardType.aggregateType === true)
    const associatedEntity = aggregateRequirement ? entities[aggregateRequirement.entityId] : null

    event.requirementsJson.forEach((req) => {
      const dataModelForReq = event.dataModels?.find(dm => dm.cardId === req.id)
      const dataFields = dataModelForReq?.dataFields.map(f => ({ name: f.name, type: f.type })) || []

      if (req.cardType.triggerType === true) {
        commands.push({ ...req, dataFields, associatedEntity })
      } else if (req.cardType.queryType === true && associatedEntity) {
        readModels.push({ ...req, dataFields, associatedEntity })
      }
    })
  })

  const mergedReadModels = groupAndMergeReadModels(readModels, Object.values(entities))

  for (const entity of Object.values(entities)) {
    const schemaName = entity.namePascal
    const properties = {}
    const required = []

    entity.dataFields.forEach((field) => {
      const propertyName = toCamelCase(field.name)
      properties[propertyName] = {
        type: 'string',
        description: field.primaryKey
          ? `Unique identifier for the ${entity.name} (primary key).`
          : `The ${field.name} of the ${entity.name}.`,
        example: field.exampleData?.[0]
      }
      if (field.tags?.includes('REQUIRED')) {
        required.push(propertyName)
      }
    })

    swaggerSpec.components.schemas[schemaName] = {
      type: 'object',
      description: `Represents a ${entity.name} in the system.`,
      properties,
      ...(required.length > 0 && { required })
    }

    if (entity.primaryKey && entity.primaryKey.nameCamel) {
      swaggerSpec.components.parameters[`${schemaName}Id`] = {
        name: entity.primaryKey.nameCamel,
        in: 'path',
        required: true,
        description: `The unique identifier for the ${entity.name}.`,
        schema: { type: 'string' }
      }
    }
  }

  commands.forEach((command) => {
    if (!command.associatedEntity) {
      return
    }

    const entity = command.associatedEntity
    const cleanDescription = sanitizeCardDescription(command.description)
    const operationId = toCamelCase(cleanDescription)
    const tag = toPascalCase(boundedContext.name)

    if (tag && !swaggerSpec.tags.find(t => t.name === tag)) {
      swaggerSpec.tags.push({ name: tag, description: `Operations related to the ${tag} bounded context.` })
    }

    const method = 'post'
    const path = descriptionToPath(cleanDescription)
    // const parameters = []

    // if (cleanDescription.toLowerCase().startsWith('update') || cleanDescription.toLowerCase().startsWith('modify')) {
    //   method = 'put'
    //   if (entity.primaryKey && entity.primaryKey.nameCamel) {
    //     path += `/{${entity.primaryKey.nameCamel}}`
    //     parameters.push({ $ref: `#/components/parameters/${entity.namePascal}Id` })
    //   }
    // } else if (cleanDescription.toLowerCase().startsWith('delete') || cleanDescription.toLowerCase().startsWith('remove')) {
    //   method = 'delete'
    //   if (entity.primaryKey && entity.primaryKey.nameCamel) {
    //     path += `/{${entity.primaryKey.nameCamel}}`
    //     parameters.push({ $ref: `#/components/parameters/${entity.namePascal}Id` })
    //   }
    // }

    const operation = {
      summary: cleanDescription,
      description: `Executes the "${cleanDescription}" command. ...`,
      operationId,
      tags: [tag],
      responses: {
        202: { description: 'Accepted: The command has been accepted for processing.' },
        400: { $ref: '#/components/responses/BadRequest' }
      }
    }

    const requestFields = command.dataFields

    if (requestFields.length > 0 && (method === 'post' || method === 'put')) {
      const requestSchemaName = `${toPascalCase(cleanDescription)}Request`
      const properties = {}
      const required = []

      requestFields.forEach((field) => {
        const fieldCamelCase = toCamelCase(field.name)
        const entityField = entity.dataFields.find(f => f.name === field.name)
        if (entityField) {
          properties[fieldCamelCase] = {
            type: 'string',
            example: entityField.exampleData?.[0]
          }
          if (entityField.tags?.includes('REQUIRED')) {
            required.push(fieldCamelCase)
          }
        } else {
          properties[fieldCamelCase] = { type: 'string' }
        }
      })

      swaggerSpec.components.schemas[requestSchemaName] = {
        type: 'object',
        properties,
        ...(required.length > 0 && { required })
      }

      operation.requestBody = {
        required: true,
        content: {
          'application/json': {
            schema: { $ref: `#/components/schemas/${requestSchemaName}` }
          }
        }
      }

      if (method === 'post') {
        operation.responses['200'] = {
          description: 'OK: The resource was updated successfully.',
          content: { 'application/json': { schema: { $ref: `#/components/schemas/${entity.namePascal}` } } }
        }
        delete operation.responses['201']
        delete operation.responses['202']
      }
    }

    if (method === 'delete') {
      operation.responses['204'] = { description: 'No Content: The resource was deleted successfully.' }
      delete operation.responses['202']
    }

    if (!swaggerSpec.paths[path]) {
      swaggerSpec.paths[path] = {}
    }
    swaggerSpec.paths[path][method] = operation
  })

  mergedReadModels.forEach((readModel) => {
    if (!readModel.associatedEntity) {
      return
    }

    const entity = readModel.associatedEntity
    const cleanDescription = sanitizeCardDescription(readModel.description)
    const operationId = toCamelCase(cleanDescription)
    const tag = toPascalCase(boundedContext.name)
    const cardinality = readModel.cardinality

    if (tag && !swaggerSpec.tags.find(t => t.name === tag)) {
      swaggerSpec.tags.push({ name: tag, description: `Operations related to the ${tag} bounded context.` })
    }

    const method = 'get'
    let path = descriptionToPath(cleanDescription)
    let parameters = []
    const isGetById = cardinality === 'one-to-one'

    const filterTypeFields = readModel.dataFields.filter(f => f.type === 'filter')
    path += filterTypeFields.map(f => `/{${toCamelCase(f.name)}}`).join('')
    parameters = filterTypeFields.map(f => ({
      in: 'path',
      name: toCamelCase(f.name),
      required: true,
      schema: { type: 'string' }
    }))

    const operation = {
      summary: cleanDescription,
      description: `Retrieves data based on the "${cleanDescription}" query. This is a synchronous query operation.`,
      operationId,
      tags: [tag],
      responses: {
        400: { $ref: '#/components/responses/BadRequest' }
      }
    }

    if (parameters.length > 0) {
      operation.parameters = parameters
    }

    const successResponse = {
      description: 'Successful retrieval of data.',
      content: { 'application/json': { schema: {} } }
    }

    if (isGetById) {
      successResponse.content['application/json'].schema = { $ref: `#/components/schemas/${entity.namePascal}` }
    } else {
      successResponse.content['application/json'].schema = {
        type: 'array',
        items: { $ref: `#/components/schemas/${entity.namePascal}` }
      }
    }
    operation.responses['200'] = successResponse

    if (!swaggerSpec.paths[path]) {
      swaggerSpec.paths[path] = {}
    }
    swaggerSpec.paths[path][method] = operation
  })

  return yaml.dump(swaggerSpec, { noRefs: false, lineWidth: -1 })
}

export const calcGitBlobHash = (content) => {
  const normalizedContent = content.replace(/\r\n/g, '\n')
  const byteLength = Buffer.byteLength(normalizedContent, 'utf8')
  const header = `blob ${byteLength}\0`
  const store = header + normalizedContent

  const sha = CryptoJS.SHA1(store).toString(CryptoJS.enc.Hex)

  return sha
}

export const diffArrays = (A, B) => {
  const mapA = new Map(A.map(item => [item.id, item]))
  const mapB = new Map(B.map(item => [item.id, item]))

  const results = []

  // Removed or updated
  for (const [id, itemA] of mapA.entries()) {
    if (!mapB.has(id)) {
      results.push({ type: 'delete', old: itemA })
    } else {
      const itemB = mapB.get(id)
      if (!_.isEqual(itemA, itemB)) {
        results.push({ type: 'update', old: itemA, new: itemB })
      }
    }
  }

  // Added
  for (const [id, itemB] of mapB.entries()) {
    if (!mapA.has(id)) {
      results.push({ type: 'create', new: itemB })
    }
  }

  return results
}
